import { typographyTokens } from '../../../theme/typographyTokens';
import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { styled } from '@mui/material/styles';
import {
  Table, TableBody, TableCell, tableCellClasses, TableContainer,
  TableHead, TableRow, Paper, Box, Typography, TablePagination, TextField
} from '@mui/material';
import SpinnerOverlay from '../../../components/Spinner/Spinner';
import { useGetNotificationLogsQuery } from '../../../services/appSlice/appSlice';
import { NotificationTable as NotificationTableType } from '../../../../shared/types/NotificationTable';

// Date picker and dayjs imports
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';

// Constants for min and max dates
const MIN_DATE = dayjs('2025-01-01');
const MAX_DATE = dayjs(); // today

// Styled Components
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: typographyTokens.fontSizes.body,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': { backgroundColor: theme.palette.action.hover },
  '&:last-child td, &:last-child th': { border: 0 },
}));

// Table Header Cells
const TABLE_HEADERS = [
  { label: 'ID', width: '2%' },
  { label: 'Notified Date', width: '15%' },
  { label: 'NotificationType', width: '13%' },
  { label: 'Sep ID', width: '7%' },
  { label: 'Task ID', width: '7%' },
  { label: 'Subject', width: '15%' },
  { label: 'TO Recipients', width: '17%' },
  { label: 'CC Recipients', width: '17%' },
];

// Helper: Render Recipients
const renderRecipients = (recipients: string | undefined) => {
  if (!recipients) return '-';
  return recipients.split(/[,;]+/).map((recipient: string, idx: number) => (
    <Typography key={idx} variant="body2">{recipient.trim()}</Typography>
  ));
};

// Date Range Filter Props
interface DateRangeFilterProps {
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  onStartDateChange: (date: Dayjs | null) => void;
  onEndDateChange: (date: Dayjs | null) => void;
}

// Date Range Filter Component using MUI DatePicker
const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  startDate, endDate, onStartDateChange, onEndDateChange,
}) => (
  <LocalizationProvider dateAdapter={AdapterDayjs}>
    <Box display="flex" gap={2}>
      <DatePicker
        label="Start Date"
        value={startDate}
        onChange={onStartDateChange}
        minDate={MIN_DATE}
        maxDate={endDate || MAX_DATE}
        slotProps={{
          textField: {
            placeholder: 'mm/dd/yyyy',
            size: 'small',
            fullWidth: true,
          },
        }}
      />
      <DatePicker
        label="End Date"
        value={endDate}
        onChange={onEndDateChange}
        minDate={startDate || MIN_DATE}
        maxDate={MAX_DATE}
        slotProps={{
          textField: {
            placeholder: 'mm/dd/yyyy',
            size: 'small',
            fullWidth: true,
          },
        }}
      />
    </Box>
  </LocalizationProvider>
);

// Main Table Component
export default function NotificationTable() {
  const {
    data: notifications,
    isError,
    isLoading,
  } = useGetNotificationLogsQuery();

  // State
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(5);
  const [searchTerm, setSearchTerm] = useState<string>('');
  // Start and end dates are empty by default (show placeholder)
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);

  // Handlers
  const handleStartDateChange = useCallback((date: Dayjs | null) => {
    setStartDate(date);
    if (endDate && date && date.isAfter(endDate, 'day')) setEndDate(null);
  }, [endDate]);

  const handleEndDateChange = useCallback((date: Dayjs | null) => {
    setEndDate(date);
    if (startDate && date && date.isBefore(startDate, 'day')) setStartDate(date);
  }, [startDate]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value), []);

  // Filtering
  const filteredNotifications = useMemo(() => {
    if (!notifications) return [];
    const lowerSearch = searchTerm.trim().toLowerCase();
    return notifications.filter((row: NotificationTableType) => {
      // Search filter
      const matchesSearch = lowerSearch
        ? [
            String(row.taskID || ''),
            String(row.NotificationType || ''),
            String(row.sepID || '')
          ].some(field => field.toLowerCase().includes(lowerSearch))
        : true;
      // Date filter
      let matchesDate = true;
      if (row.NotifiedDate) {
        const notifiedDate = dayjs(row.NotifiedDate);
        if (startDate && notifiedDate.isBefore(startDate, 'day')) matchesDate = false;
        if (endDate && notifiedDate.isAfter(endDate, 'day')) matchesDate = false;
      } else if (startDate || endDate) {
        matchesDate = false;
      }
      return matchesSearch && matchesDate;
    });
  }, [notifications, searchTerm, startDate, endDate]);

  // Pagination
  const paginatedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredNotifications.slice(start, start + rowsPerPage);
  }, [filteredNotifications, page, rowsPerPage]);

  // Reset page if out of bounds
  useEffect(() => {
    if (page > 0 && filteredNotifications && page * rowsPerPage >= filteredNotifications.length) {
      setPage(0);
    }
  }, [filteredNotifications, page, rowsPerPage]);

  // Loading/Error States
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" flexGrow={1} py="24px">
        <SpinnerOverlay />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" flexGrow={1} py="24px">
        <Typography color="error.main" fontSize={typographyTokens.fontSizes.h5}>Error</Typography>
      </Box>
    );
  }

  // Main Render
  return (
    <>
      {/* Filters */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <DateRangeFilter
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={handleStartDateChange}
          onEndDateChange={handleEndDateChange}
        />
        <TextField
          label="Search ( Notification Type, Sep ID, Task ID )"
          value={searchTerm}
          onChange={handleSearchChange}
          size="small"
          sx={{ minWidth: 400, maxWidth: 600 }}
          inputProps={{ maxLength: 100 }}
        />
      </Box>
      {/* Table */}
      <TableContainer component={Paper} sx={{ maxHeight: 600, minHeight: 600 }}>
        <Table stickyHeader sx={{ minWidth: 700 }} aria-label="customized table">
          <TableHead>
            <TableRow>
              {TABLE_HEADERS.map((header) => (
                <StyledTableCell key={header.label} sx={{ width: header.width }}>
                  {header.label}
                </StyledTableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedRows.length ? paginatedRows.map((row: NotificationTableType, index: number) => (
              <StyledTableRow key={row.id || index}>
                <StyledTableCell sx={{ width: '2%' }}>{row.id}</StyledTableCell>
                <StyledTableCell sx={{ width: '15%' }}>
                  {row.NotifiedDate ? dayjs(row.NotifiedDate).format('MM/DD/YYYY HH:mm:ss') : '-'}
                </StyledTableCell>
                <StyledTableCell sx={{ width: '13%' }}>{row.NotificationType}</StyledTableCell>
                <StyledTableCell sx={{ width: '7%' }}>{row.sepID}</StyledTableCell>
                <StyledTableCell sx={{ width: '7%' }}>{row.taskID || '-'}</StyledTableCell>
                <StyledTableCell sx={{ width: '15%' }}>{row.Subject}</StyledTableCell>
                <StyledTableCell sx={{ width: '17%' }}>{renderRecipients(row.ToRecipients)}</StyledTableCell>
                <StyledTableCell sx={{ width: '17%' }}>{renderRecipients(row.CCRecipients)}</StyledTableCell>
              </StyledTableRow>
            )) : (
              <TableRow>
                <TableCell colSpan={8}>
                  <Typography color="error.main" fontSize={typographyTokens.fontSizes.xl} padding="17px 0PX 0px 20px">
                    No record found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {/* Pagination */}
      <Box sx={{ margin: '16px 0px 10px 0px' }}>
        <TablePagination
          component="div"
          count={filteredNotifications.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Box>
    </>
  );
}
 